import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Table } from '../../types/domain';
import { ArrowRight, Inbox, HardDrive } from 'lucide-react';
import { formatFileSize } from '../../utils/fileUrl';

export interface DatastoreCardProps {
  table: Table;
  orgId?: string;
  projectId?: string;
}

export const DatastoreCard: React.FC<DatastoreCardProps> = ({ table, orgId, projectId }) => {
  const navigate = useNavigate();
  const targetRoute =
    orgId && projectId
      ? `/organization/${orgId}/projects/${projectId}/tables/${table.id}`
      : `/tables/${table.id}`;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => navigate(targetRoute)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          navigate(targetRoute);
        }
      }}
      className="p-4 rounded-xl bg-cf-surface border border-cf-border hover:border-zinc-600 dark:hover:border-zinc-500 hover:bg-cf-surfaceHover/40 transition-all flex flex-col justify-between cursor-pointer group text-left select-none outline-none focus:border-zinc-500"
    >
      <div className="flex items-start justify-between gap-2">
        <h3 className="text-sm font-bold text-cf-textPrimary group-hover:text-cf-orange transition-colors truncate">
          {table.name}
        </h3>
        <ArrowRight
          size={14}
          className="text-cf-textSecondary group-hover:text-cf-textPrimary group-hover:translate-x-0.5 transition-all shrink-0 mt-0.5"
        />
      </div>

      <div className="mt-3.5 pt-2.5 border-t border-cf-border/60 flex items-center justify-between text-xs">
        <div
          className="flex items-center gap-1.5 min-w-0"
          title={`${(table.submission_count ?? 0).toLocaleString()} submissions`}
        >
          <Inbox size={13} className="text-cf-textMuted shrink-0" />
          <span className="font-semibold text-cf-textPrimary font-mono tabular-nums truncate">
            {(table.submission_count ?? 0).toLocaleString()}
          </span>
        </div>

        <div className="h-3 w-px bg-cf-border shrink-0" />

        <div
          className="flex items-center gap-1.5 min-w-0"
          title={`${formatFileSize(table.storage_bytes || 0)} storage used`}
        >
          <HardDrive size={13} className="text-cf-textMuted shrink-0" />
          <span className="font-semibold text-cf-textPrimary font-mono tabular-nums whitespace-nowrap">
            {formatFileSize(table.storage_bytes || 0)}
          </span>
        </div>
      </div>
    </div>
  );
};
